import { useState } from 'react'
import { LayoutGrid, Crown, AlertCircle, Settings, BookOpen } from 'lucide-react'
import { Link } from 'react-router-dom'
import { SidebarTrigger } from './ui/sidebar'
import { Separator } from './ui/separator'
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu'
import { Button } from './ui/button'
import TeamSwitcher from './TeamSwitcher'
import { UserSettings } from './UserSettings'
import { NotificationBell } from './NotificationBell'
import { useAuth } from '../contexts/AuthContext'

interface TopBarProps {
  teamId?: string
  teamName?: string
  onLogout: () => void
  onUpgrade?: () => void
  showUpgrade?: boolean
  upgradeUrgent?: boolean
  /** Breadcrumb label — defaults to "Dashboard", pass e.g. "Standups" on other pages. */
  sectionLabel?: string
  /**
   * The signed-in user's own users.avatar_url. Not fetched by TopBar itself
   * — none of this component's current callers (TeamDashboard,
   * UserStandupHistory, TeamAgents, InstallClaudeCode) load the current
   * user's own row today, only team-scoped data, so wiring a real photo
   * through end-to-end is a separate follow-up. Omit it and the account
   * menu falls back to the team-name initial, same as before.
   */
  avatarUrl?: string | null
}

/** Top bar matching syncsimple-v2's TopBar: sidebar trigger + breadcrumb on the
 * left, team switcher/upgrade/notifications/account menu on the right. */
export function TopBar({
  teamId,
  teamName,
  onLogout,
  onUpgrade,
  showUpgrade,
  upgradeUrgent,
  sectionLabel = 'Dashboard',
  avatarUrl,
}: TopBarProps) {
  const [settingsOpen, setSettingsOpen] = useState(false)
  const { user } = useAuth()
  const userMetadata = user?.user_metadata || {}
  const userName = userMetadata.name ||
    userMetadata.full_name ||
    [userMetadata.given_name, userMetadata.family_name].filter(Boolean).join(' ') ||
    user?.email?.split('@')[0] ||
    'Signed-in user'
  const userEmail = user?.email || 'No email available'
  const userAvatarUrl = avatarUrl || userMetadata.picture || userMetadata.avatar_url || null
  const userInitials = userName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part: string) => part.charAt(0).toUpperCase())
    .join('') || 'U'

  return (
    <div className="flex items-center justify-between border-b bg-background/95 px-4 py-3 sm:px-6">
      <div className="flex items-center gap-3 text-sm text-muted-foreground">
        <SidebarTrigger />
        <Separator orientation="vertical" className="h-4" />
        <LayoutGrid className="h-4 w-4" />
        <span className="hidden font-medium text-foreground sm:inline">{sectionLabel}</span>
      </div>

      <div className="flex items-center gap-3">
        {(teamId || teamName) && (
          <TeamSwitcher currentTeamId={teamId} currentTeamName={teamName} />
        )}

        {showUpgrade && onUpgrade && (
          <Button
            size="sm"
            onClick={onUpgrade}
            className={
              upgradeUrgent
                ? 'rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90'
                : 'rounded-full'
            }
          >
            {upgradeUrgent ? (
              <AlertCircle className="mr-1.5 h-3.5 w-3.5" />
            ) : (
              <Crown className="mr-1.5 h-3.5 w-3.5" />
            )}
            {upgradeUrgent ? 'Upgrade Now' : 'Upgrade Plan'}
          </Button>
        )}

        <NotificationBell teamId={teamId} />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Account menu"
              className="size-11 rounded-full p-0"
            >
              <Avatar className="h-8 w-8">
                {userAvatarUrl && <AvatarImage src={userAvatarUrl} alt="" />}
                <AvatarFallback className="text-xs">
                  {userInitials}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <div className="px-2 py-1.5">
              <div className="text-sm font-semibold">{userName}</div>
              <div className="max-w-56 truncate text-xs text-muted-foreground">{userEmail}</div>
            </div>
            <DropdownMenuSeparator />
            {teamId && <DropdownMenuItem asChild>
              <Link to={`/dashboard/team/${teamId}?setup=resume`}>
                <BookOpen className="mr-2 h-4 w-4" />
                Setup guide
              </Link>
            </DropdownMenuItem>}
            <DropdownMenuItem onClick={() => setSettingsOpen(true)}>
              <Settings className="mr-2 h-4 w-4" />
              Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onLogout}>Log out</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <UserSettings isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </div>
  )
}
